import { html, unsafeCSS } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_DEVICE_CONTROL_RULES } from '../../../../../../../webcomponents.constants';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  DeviceControlRulesFormValue,
  DeviceControlRulesSearchableKeywords,
} from '../../../../utils/uiSettings/deviceControl/device-control.types';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import {
  getDeviceControlRulesDefaultValue,
  getDeviceControlRulesSearchableKeywords,
} from '../../../../utils/uiSettings/deviceControl/device-control.values';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import _ from 'lodash';
import { GzDeviceControlRuleGridWebComponent } from '../gridComponents/GzDeviceControlRuleGridWebComponent';
import { DeviceControlRuleItem } from '@nirvana/shared/dtos/types/policies/common';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';
import { styles as specificAccordionStyles } from '../view/view.scss';

@customElement(WEB_COMPONENT_POLICY_PANEL_DEVICE_CONTROL_RULES)
export class DeviceControlRulesWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  DeviceControlRulesFormValue,
  DeviceControlRulesSearchableKeywords
> {
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  protected path = {
    moduleId: ModuleId.DEVICE_CONTROL,
    sectionId: SectionId.RULES,
  };

  protected searchableKeywords = getDeviceControlRulesSearchableKeywords();

  @query('#device-control-rules-toggle')
  private deviceControlToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#device-control-rules-info-text')
  private deviceControlInfoText: GzTextWebComponent;

  @query('#device-control-rules-content')
  private deviceControlContent: HTMLElement;

  @query('#rules-grid-form-field')
  private rulesGridFormField: GzFormFieldWebComponent<GzDeviceControlRuleGridWebComponent, DeviceControlRuleItem[]>;

  protected renderTitle() {
    return html`<gz-form-field id="device-control-rules-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[DeviceControlRulesSearchableKeywords.DeviceControl].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span>${this.searchableKeywords[DeviceControlRulesSearchableKeywords.DeviceControl].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="device-control-rules-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn('Use Data Protection rules to prevent unauthorized disclosure of sensitive data.')}
    </gz-text>`;
  }

  protected renderContent() {
    return html`
      <div id="device-control-rules-content" class="width-100">
        <gz-form-field
          id="rules-grid-form-field"
          searchable-label="${this.searchableKeywords[DeviceControlRulesSearchableKeywords.Bluetooth].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.CDROMDrive].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.FloppyDiskDrive].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.IEEE1284_4].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.IEEE1394].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.Imaging].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.Modem].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.TapeDrive].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.WindowsPortable].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.COMLPTPorts].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.SCSIRaid].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.Printers].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.NetworkAdapter].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.WirelessNetworkAdapter].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.InternalStorage].label +
          ' ' +
          this.searchableKeywords[DeviceControlRulesSearchableKeywords.ExternalStorage].label}"
          label=""
        >
          <gz-policy-panel-device-control-rule-table gz-form-input> </gz-policy-panel-device-control-rule-table>
        </gz-form-field>
      </div>
    `;
  }

  protected createFormGroup(): void {
    this.formGroup = FormGroup.build({
      uiSettings: {
        deviceControl: {
          dataLossPrevention: {
            enable: this.deviceControlToggle,
            rules: this.rulesGridFormField,
          },
        },
      },
    });
  }

  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);
    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'deviceControl', 'dataLossPrevention'], {});
    }
    return value;
  }

  protected getValueFromManager(): DeviceControlRulesFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.DEVICE_CONTROL]: {
          dataLossPrevention: getDeviceControlRulesDefaultValue(),
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.DEVICE_CONTROL]: {
          dataLossPrevention: PolicyPanelManager.getInstance().getPolicy('uiSettings.deviceControl.dataLossPrevention'),
        },
      },
    };

    const overwriteMerge = (
      destinationArray: DeviceControlRuleItem[],
      sourceArray: DeviceControlRuleItem[],
      options
    ) => {
      return sourceArray.map((sourceItem, index) => {
        return deepmerge(destinationArray[index], sourceItem, options);
      });
    };

    return deepmerge(defaultValue, value, { arrayMerge: overwriteMerge });
  }

  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }
    // set form group value
    this.formGroup.value = {
      uiSettings: {
        deviceControl: {
          dataLossPrevention: {
            enable: this.valueT.uiSettings.deviceControl.dataLossPrevention.enable,
            rules: this.valueT.uiSettings.deviceControl.dataLossPrevention.rules,
          },
        },
      },
    };
  }
  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.deviceControlInfoText, !event.detail.checked);
    toggleDisabled(this.deviceControlContent, !event.detail.checked);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_DEVICE_CONTROL_RULES: DeviceControlRulesWebComponent;
  }
}
